// src/services/api.js - Complete API Service with Caching and Retry
const API_BASE = process.env.REACT_APP_API_URL || '/api';

class ApiService {
    constructor() {
        // ✅ ADD CACHE
        this.cache = new Map();
        this.cacheTimeout = 5000; // 5 seconds
        this.maxRetries = 3;
        this.baseDelay = 1000; // 1 second
    }

    // ✅ CACHE METHODS
    getCached(key) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
            console.log('✅ Using cached data for:', key);
            return cached.data;
        }
        return null;
    }

    setCached(key, data) {
        this.cache.set(key, { data, timestamp: Date.now() });
    }

    clearCache() {
        this.cache.clear();
        console.log('🗑️ Cache cleared');
    }

    // ✅ RETRY WITH EXPONENTIAL BACKOFF
    async sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async requestWithRetry(endpoint, options = {}, retryCount = 0) {
        try {
            return await this.request(endpoint, options);
        } catch (error) {
            if (retryCount < this.maxRetries) {
                const delay = this.baseDelay * Math.pow(2, retryCount);
                console.log(`⏳ Retry ${retryCount + 1}/${this.maxRetries} in ${delay}ms...`);
                await this.sleep(delay);
                return this.requestWithRetry(endpoint, options, retryCount + 1);
            }
            throw error;
        }
    }

    async request(endpoint, options = {}) {
        const url = `${API_BASE}${endpoint}`;
        
        try {
            const response = await fetch(url, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers
                }
            });
            
            if (!response.ok) {
                const errBody = await response.json().catch(() => ({ error: response.statusText }));
                const err = new Error(errBody.error || errBody.message || 'API request failed');
                err.status = response.status;
                err.body = errBody;
                throw err;
            }
            
            if (response.status === 204) return null;
            return response.json();
        } catch (error) {
            console.error(`API Error [${endpoint}]:`, error);
            throw error;
        }
    }

    // ✅ BULK DATA FETCH - ONE CALL FOR EVERYTHING (WITH RETRY)
    async getAllData(forceRefresh = false) {
        const cacheKey = 'bulkData';

        if (!forceRefresh) {
            const cached = this.getCached(cacheKey);
            if (cached) return cached;
        }

        console.log('🔄 Fetching fresh data from server...');
        const data = await this.requestWithRetry('/bulkData');
        this.setCached(cacheKey, data);
        console.log('✅ Data cached at:', data.timestamp);
        return data;
    }

    // ==================== DEALS ====================
    getDeals = () => this.request('/deals');
    getDeal = (id) => this.request(`/deals/${id}`);
    
    createDeal = async (data) => {
        const result = await this.request('/deals', { 
            method: 'POST', 
            body: JSON.stringify(data) 
        });
        this.clearCache(); // ✅ Clear cache after mutation
        return result;
    };
    
    updateDeal = async (id, data) => {
        const result = await this.request(`/deals/${id}`, { 
            method: 'PUT', 
            body: JSON.stringify(data) 
        });
        this.clearCache(); // ✅ Clear cache after mutation
        return result;
    };
    
    deleteDeal = async (id) => {
        const result = await this.request(`/deals/${id}`, { method: 'DELETE' });
        this.clearCache(); // ✅ Clear cache after mutation
        return result;
    };

    // ==================== ACTIVITIES ====================
    getActivities = () => this.request('/activities');
    getActivity = (id) => this.request(`/activities/${id}`);
    
    createActivity = async (data) => {
        const result = await this.request('/activities', { 
            method: 'POST', 
            body: JSON.stringify(data) 
        });
        this.clearCache();
        return result;
    };
    
    updateActivity = async (id, data) => {
        const result = await this.request(`/activities/${id}`, { 
            method: 'PUT', 
            body: JSON.stringify(data) 
        });
        this.clearCache();
        return result;
    };
    
    deleteActivity = async (id) => {
        const result = await this.request(`/activities/${id}`, { method: 'DELETE' });
        this.clearCache();
        return result;
    };

    // ==================== INVESTORS ====================
    getInvestors = () => this.request('/investors');
    getInvestor = (id) => this.request(`/investors/${id}`);
    
    createInvestor = async (data) => {
        const result = await this.request('/investors', { 
            method: 'POST', 
            body: JSON.stringify(data) 
        });
        this.clearCache();
        return result;
    };
    
    updateInvestor = async (id, data) => {
        const result = await this.request(`/investors/${id}`, { 
            method: 'PUT', 
            body: JSON.stringify(data) 
        });
        this.clearCache();
        return result;
    };
    
    deleteInvestor = async (id) => {
        const result = await this.request(`/investors/${id}`, { method: 'DELETE' });
        this.clearCache();
        return result;
    };

    // ==================== PARTNERSHIPS ====================
    getPartnerships = () => this.request('/partnerships');
    getPartnership = (id) => this.request(`/partnerships/${id}`);
    
    createPartnership = async (data) => {
        const result = await this.request('/partnerships', { 
            method: 'POST', 
            body: JSON.stringify(data) 
        });
        this.clearCache();
        return result;
    };
    
    updatePartnership = async (id, data) => {
        const result = await this.request(`/partnerships/${id}`, { 
            method: 'PUT', 
            body: JSON.stringify(data) 
        });
        this.clearCache();
        return result;
    };
    
    deletePartnership = async (id) => {
        const result = await this.request(`/partnerships/${id}`, { method: 'DELETE' });
        this.clearCache();
        return result;
    };

    // ==================== INTERNAL TEAM ====================
    getInternal = () => this.request('/internal');
    getInternalPerson = (id) => this.request(`/internal/${id}`);
    
    createInternalPerson = async (data) => {
        const result = await this.request('/internal', { 
            method: 'POST', 
            body: JSON.stringify(data) 
        });
        this.clearCache();
        return result;
    };
    
    updateInternalPerson = async (id, data) => {
        const result = await this.request(`/internal/${id}`, { 
            method: 'PUT', 
            body: JSON.stringify(data) 
        });
        this.clearCache();
        return result;
    };
    
    deleteInternalPerson = async (id) => {
        const result = await this.request(`/internal/${id}`, { method: 'DELETE' });
        this.clearCache();
        return result;
    };

    // Backward compatibility aliases
    getSalesPeople = () => this.getInternal();
    getSalesPerson = (id) => this.getInternalPerson(id);
    createSalesPerson = (data) => this.createInternalPerson(data);
    updateSalesPerson = (id, data) => this.updateInternalPerson(id, data);
    deleteSalesPerson = (id) => this.deleteInternalPerson(id);

    // ==================== EXTERNAL CONTACTS ====================
    getExternalContacts = () => this.request('/externalcontacts');
    getExternalContact = (id) => this.request(`/externalcontacts/${id}`);
    
    createExternalContact = async (data) => {
        const result = await this.request('/externalcontacts', { 
            method: 'POST', 
            body: JSON.stringify(data) 
        });
        this.clearCache();
        return result;
    };
    
    updateExternalContact = async (id, data) => {
        const result = await this.request(`/externalcontacts/${id}`, { 
            method: 'PUT', 
            body: JSON.stringify(data) 
        });
        this.clearCache();
        return result;
    };
    
    deleteExternalContact = async (id) => {
        const result = await this.request(`/externalcontacts/${id}`, { method: 'DELETE' });
        this.clearCache();
        return result;
    };

    // Backward compatibility aliases
    getContacts = () => this.getExternalContacts();
    getContact = (id) => this.getExternalContact(id);
    createContact = (data) => this.createExternalContact(data);
    updateContact = (id, data) => this.updateExternalContact(id, data);
    deleteContact = (id) => this.deleteExternalContact(id);

    // ==================== DEAL ASSIGNMENTS ====================
    getSalesPersonAssignments = (salesPersonId) => {
        return this.request(`/dealassignments/${salesPersonId}`);
    };
    
    getDealAssignments = (dealId) => {
        return this.request(`/dealassignments/null/${dealId}`);
    };
    
    assignDealToSalesPerson = async (salesPersonId, dealId, role = 'Primary') => {
        const result = await this.request('/dealassignments', {
            method: 'POST',
            body: JSON.stringify({ salesPersonId, dealId, role })
        });
        this.clearCache();
        return result;
    };
    
    updateSalesPersonAssignments = async (salesPersonId, dealIds) => {
        const result = await this.request(`/dealassignments/${salesPersonId}`, {
            method: 'PUT',
            body: JSON.stringify({ dealIds })
        });
        this.clearCache();
        return result;
    };
    
    removeAssignment = async (salesPersonId, dealId) => {
        const result = await this.request(`/dealassignments/${salesPersonId}/${dealId}`, {
            method: 'DELETE'
        });
        this.clearCache();
        return result;
    };

    // ==================== AI TASK PARSING ====================
    parseTaskWithAI = async (transcript, internalPeople = [], conversationHistory = [], currentTask = {}) => {
        return this.request('/parseTask', {
            method: 'POST',
            body: JSON.stringify({
                transcript,
                internalPeople,
                conversationHistory,
                currentTask
            })
        });
    };

    // ==================== GTM ACCOUNTS ====================
    getGTMAccounts = () => this.request('/gtm-accounts');
    getGTMAccount = (id) => this.request(`/gtm-accounts/${id}`);
    createGTMAccount = async (data) => { const result = await this.request('/gtm-accounts', { method: 'POST', body: JSON.stringify(data) }); this.clearCache(); return result; };
    updateGTMAccount = async (id, data) => { const result = await this.request(`/gtm-accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) }); this.clearCache(); return result; };
    deleteGTMAccount = async (id) => { const result = await this.request(`/gtm-accounts/${id}`, { method: 'DELETE' }); this.clearCache(); return result; };

    // ==================== GTM KEY PEOPLE ====================
    getGTMKeyPeople = (accountId) => this.request(`/gtm-keypeople?accountId=${accountId}`);
    createGTMKeyPerson = async (data) => { const result = await this.request('/gtm-keypeople', { method: 'POST', body: JSON.stringify(data) }); this.clearCache(); return result; };
    deleteGTMKeyPerson = async (id) => { const result = await this.request(`/gtm-keypeople/${id}`, { method: 'DELETE' }); this.clearCache(); return result; };

    // ==================== GTM WARM CONTACTS ====================
    getGTMWarmContacts = (accountId) => this.request(`/gtm-warmcontacts?accountId=${accountId}`);
    createGTMWarmContact = async (data) => { const result = await this.request('/gtm-warmcontacts', { method: 'POST', body: JSON.stringify(data) }); this.clearCache(); return result; };
    deleteGTMWarmContact = async (id) => { const result = await this.request(`/gtm-warmcontacts/${id}`, { method: 'DELETE' }); this.clearCache(); return result; };

    // ==================== USE CASES ====================
    getUseCases = () => this.request('/use-cases');
    getUseCase = (id) => this.request(`/use-cases/${id}`);
    createUseCase = async (data) => { const result = await this.request('/use-cases', { method: 'POST', body: JSON.stringify(data) }); this.clearCache(); return result; };
    updateUseCase = async (id, data) => { const result = await this.request(`/use-cases/${id}`, { method: 'PUT', body: JSON.stringify(data) }); this.clearCache(); return result; };
    deleteUseCase = async (id) => { const result = await this.request(`/use-cases/${id}`, { method: 'DELETE' }); this.clearCache(); return result; };
}

const apiService = new ApiService();
export default apiService;